import { inject, Injectable } from '@angular/core';
import { App } from '@df/core/app';
import isEqual from 'lodash-es/isEqual';
import { Router, NavigationEnd } from '@angular/router';

interface FocusTrapRule {
    exclude: (Element | string)[];
    beforeElement: HTMLElement;
}

/**
 * Service for managing ARIA attributes to control accessibility of DOM elements.
 * Provides functionality to hide elements from assistive technology and restore them later.
 *
 * This service extracts the logic from ModalService's hideNonModalContentFromAssistiveTechnology
 * method to provide a reusable solution for any component that needs to block elements
 * outside of a given container from assistive technology.
 */
@Injectable({
    providedIn: 'root'
})
export class FocusTrapService {
    private activeRule: FocusTrapRule | undefined;
    private removeListener?: () => void;
    private app = inject(App);
    private router = inject(Router);

    private sequence: FocusTrapRule[] = [];
    private beforeTrapActiveElement?: HTMLElement;

    constructor() {
        // Restore focus to body after every router navigation
        if (this.app.isPlatformBrowser) {
            this.router.events.subscribe(event => {
                if (event instanceof NavigationEnd) {
                    // Ensure body is focusable
                    if (document.body.tabIndex === undefined || document.body.tabIndex < 0) {
                        document.body.tabIndex = -1;
                    }
                    document.body.focus();
                }
            });
        }
    }

    private setListener() {
        if (this.removeListener || !this.app.isPlatformBrowser) {
            return;
        }

        const checkTabKey = (event: KeyboardEvent) => {
            if (event.key === 'Tab') {
                event.preventDefault();
                event.stopImmediatePropagation();
                const isShiftPressed = event.shiftKey;
                this.focusNextElement(isShiftPressed);
            }
        };

        document.addEventListener('keydown', checkTabKey, {
            capture: true,
            passive: false
        });

        this.removeListener = () => {
            document.removeEventListener('keydown', checkTabKey, {
                capture: true
            });
            this.removeListener = undefined;
        };
    }

    lock(exclude: (Element | string)[], options?: { returnElement?: HTMLElement }): void {
        const index = this.getSequenceIndex(exclude);
        if (index === -1) {
            this.sequence.push({ exclude, beforeElement: options?.returnElement || (document.activeElement as HTMLElement) });
            this.onSequenceChange();
        }
    }

    unlock(exclude: (Element | string)[], silent = false): void {
        const index = this.getSequenceIndex(exclude);
        if (index > -1) {
            this.sequence.splice(index, 1);
        }
        this.onSequenceChange(silent);
    }

    private onSequenceChange(silent = false) {
        if (!this.app.isPlatformBrowser) {
            return;
        }
        this.activeRule = this.sequence[this.sequence.length - 1];
        if (this.activeRule) {
            this.beforeTrapActiveElement = this.activeRule.beforeElement;

            this.setListener();
        } else {
            this.removeListener?.();
            if (this.beforeTrapActiveElement) {
                if (!silent) {
                    this.beforeTrapActiveElement.focus();
                }
                this.beforeTrapActiveElement = undefined;
            }
        }
    }

    private getSequenceIndex(exclude: (Element | string)[]): number {
        return this.sequence.findIndex(item => isEqual(item.exclude, exclude));
    }

    private getTrapElementsFromRule(rule: FocusTrapRule): Element[] {
        const normalizedExclude: Element[] = [];

        rule.exclude.forEach(item => {
            if (item instanceof Element) {
                normalizedExclude.push(item);
            } else {
                normalizedExclude.push(...Array.from(document.querySelectorAll(item)));
            }
        });
        return normalizedExclude;
    }

    focusNextElement(reverse = false, within?: Element[]) {
        //add all elements we want to include in our selection
        const focusableElements: Element[] = [];
        if (!within && this.activeRule) {
            within = this.getTrapElementsFromRule(this.activeRule);
        }
        if (!within) {
            return;
        }
        within.forEach(element => {
            focusableElements.push(
                ...Array.from(
                    element.querySelectorAll(
                        'a:not([disabled]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input[type=text]:not([disabled]):not([tabindex="-1"]), [tabindex]:not([disabled]):not([tabindex="-1"])'
                    )
                )
            );
        });
        const activeElement = document.activeElement;
        const activeElementIndex = activeElement ? focusableElements.indexOf(activeElement) : -1;

        const lastIndex = focusableElements.length - 1;
        if (lastIndex < 0) {
            return;
        }
        const step = reverse ? -1 : 1;
        const startIndex = activeElementIndex > -1 ? activeElementIndex + step : reverse ? lastIndex : 0;

        let i = startIndex;
        do {
            const element = focusableElements[i];
            if (element && element instanceof HTMLElement && element.offsetHeight > 0 && element.offsetWidth > 0) {
                element.focus();
                break;
            }
            i += step;
            if (i > lastIndex) {
                i = 0;
            } else if (i < 0) {
                i = lastIndex;
            }
        } while (i !== startIndex);
    }
}
